import { Box, IconButton, Popper, useMediaQuery, useTheme } from '@mui/material';
import { sessionSelector } from 'app/store/session/selectors';
import { ChatBox } from 'modules/chat/components/ChatBox';
import { PopupParticipantCard } from 'modules/chat/components/ChatPopup/PopupParticipantCard';
import { ChatBoxContext, ChatSessionContext } from 'modules/chat/providers';
import React, { FC, useCallback, useContext, useRef, MouseEvent, useEffect } from 'react';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Loader } from 'shared/components/Loader';
import { useStyles } from './ChatPopup.styles';

export const ChatPopup: FC = () => {
  const theme = useTheme();
  const { classes } = useStyles();
  const isChatPinned = useSelector(sessionSelector.isChatPinned);
  const ref = useRef<HTMLButtonElement | null>(null);
  const isFullScreen = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { talkLoaded } = useContext(ChatSessionContext);
  const {
    conversation,
    isLoaded,
    participant,
    isPopupOpen,
    isPopupHidden,
    setIsPopupHidden,
    setIsPopupOpen,
  } = useContext(ChatBoxContext);

  const hidePopupHandler = useCallback(
    () => setIsPopupHidden((prevState) => !prevState),
    [setIsPopupHidden]
  );

  const onCloseHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsPopupHidden(false);
      setIsPopupOpen(false);
    },
    [setIsPopupHidden, setIsPopupOpen]
  );

  useEffect(() => {
    if (!isChatPinned) {
      setIsPopupHidden(true);
    }
  }, [isChatPinned, setIsPopupHidden]);

  if (!isFullScreen || (isFullScreen && !isChatPinned) || !isPopupOpen) {
    return null;
  }

  return (
    <Box>
      <Box className={classes.charPreviewContainer} ref={ref} onClick={hidePopupHandler}>
        <PopupParticipantCard user={participant} />
        <Box>
          <IconButton>
            {isPopupHidden ? (
              <KeyboardArrowUpIcon fontSize="medium" />
            ) : (
              <KeyboardArrowDownIcon fontSize="medium" />
            )}
          </IconButton>
          <IconButton onClick={onCloseHandler}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
      </Box>
      <Popper
        keepMounted
        open={!isPopupHidden && Boolean(ref.current)}
        placement="top-end"
        anchorEl={ref.current}
        className={classes.popper}
      >
        <Box className={classes.content}>
          {(!talkLoaded || !isLoaded) && (
            <Box display="flex" height="100%" justifyContent="center">
              <Loader />
            </Box>
          )}
          {talkLoaded && isLoaded && <ChatBox conversationId={conversation} />}
        </Box>
      </Popper>
    </Box>
  );
};
